<template>
  <div class="min-h-screen flex items-center justify-center relative" style="background-color: var(--bg-primary);">
    <div class="absolute top-4 right-4 z-10">
      <LanguageSwitcher variant="compact" :show-label="false" />
    </div>
    <div class="max-w-md w-full space-y-8 p-8">
      <!-- Logo e título -->
      <div class="text-center">
        <div class="mx-auto h-96 w-96 flex items-center justify-center">
          <img 
            :src="logoImage" 
            :alt="t('auth.register.logoAlt')" 
            class="h-96 w-96 object-contain"
          />
        </div>
        <div class="mt-6 flex justify-center">
          <img 
            :src="logoTextImage" 
            :alt="t('auth.register.logoTextAlt')" 
            class="h-12 object-contain"
          />
        </div>
        <p class="mt-2 text-sm" style="color: var(--text-secondary);">
          {{ t('auth.register.title') }}
        </p>
      </div>

      <!-- Formulário de cadastro -->
      <form @submit.prevent="handleRegister" class="mt-8 space-y-6">
        <div class="space-y-4">
          <div>
            <label for="nomeCompleto" class="block text-sm font-medium mb-2" style="color: var(--text-primary);">
              {{ t('auth.register.fullName') }}
            </label>
            <input
              id="nomeCompleto"
              v-model="form.nomeCompleto"
              type="text"
              required
              class="input-field"
              :placeholder="t('auth.register.fullNamePlaceholder')"
              :disabled="authStore.isLoading"
            />
          </div>
          
          <div>
            <label for="email" class="block text-sm font-medium mb-2" style="color: var(--text-primary);">
              {{ t('auth.register.email') }}
            </label>
            <input
              id="email"
              v-model="form.email"
              type="email"
              required
              class="input-field"
              :placeholder="t('auth.register.emailPlaceholder')"
              :disabled="authStore.isLoading"
            />
          </div>
          
          <div>
            <label for="password" class="block text-sm font-medium mb-2" style="color: var(--text-primary);">
              {{ t('auth.register.password') }}
            </label>
            <input
              id="password"
              v-model="form.password"
              type="password"
              required
              minlength="6"
              class="input-field"
              :placeholder="t('auth.register.passwordPlaceholder')"
              :disabled="authStore.isLoading"
            />
          </div>
          
          <div>
            <label for="confirmPassword" class="block text-sm font-medium mb-2" style="color: var(--text-primary);">
              {{ t('auth.register.confirmPassword') }}
            </label>
            <input
              id="confirmPassword"
              v-model="form.confirmPassword"
              type="password"
              required
              class="input-field"
              :placeholder="t('auth.register.confirmPasswordPlaceholder')"
              :disabled="authStore.isLoading"
            />
          </div>
        </div>

        <div class="flex items-center">
          <input
            id="terms"
            v-model="form.acceptTerms"
            type="checkbox"
            required
            class="h-4 w-4 text-discord-blurple focus:ring-discord-blurple border-discord-dark-200 rounded"
          />
          <label for="terms" class="ml-2 block text-sm" style="color: var(--text-secondary);">
            {{ t('auth.register.acceptTermsPrefix') }}
            <a href="#" style="color: var(--info);">{{ t('auth.register.termsOfUse') }}</a>
            {{ t('auth.register.acceptTermsAnd') }}
            <a href="#" style="color: var(--info);">{{ t('auth.register.privacyPolicy') }}</a>
          </label>
        </div>

        <div>
          <button
            type="submit"
            :disabled="authStore.isLoading || !isFormValid"
            class="w-full btn-primary py-3 text-base font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span v-if="authStore.isLoading" class="flex items-center justify-center">
              <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {{ t('auth.register.submitting') }}
            </span>
            <span v-else>{{ t('auth.register.submit') }}</span>
          </button>
        </div>

        <div class="text-center">
          <p class="text-sm" style="color: var(--text-secondary);">
            {{ t('auth.register.hasAccount') }}
            <router-link to="/login" class="font-medium" style="color: var(--info);">
              {{ t('auth.register.loginLink') }}
            </router-link>
          </p>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { reactive, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '../stores/auth'
import { useThemeStore } from '../stores/theme'
import LanguageSwitcher from '../components/LanguageSwitcher.vue'
import logoLight from '../assets/logo_b.png'
import logoDark from '../assets/logo_w.png'
import logoTextLight from '../assets/logo_l_p.png'
import logoTextDark from '../assets/logo_l_w.png'
import { trackMeta } from '../utils/metaPixel'

const router = useRouter()
const authStore = useAuthStore()
const themeStore = useThemeStore()
const { t } = useI18n()

const form = reactive({
  nomeCompleto: '',
  email: '',
  password: '',
  confirmPassword: '',
  acceptTerms: false
})

const isLightTheme = computed(() => themeStore.isLight)
const logoImage = computed(() => {
  return isLightTheme.value ? logoLight : logoDark
})
const logoTextImage = computed(() => {
  return isLightTheme.value ? logoTextLight : logoTextDark
})

const isFormValid = computed(() => {
  return form.nomeCompleto && 
         form.email && 
         form.password && 
         form.password === form.confirmPassword && 
         form.acceptTerms
})

const handleRegister = async () => {
  if (!isFormValid.value) return

  trackMeta('InitiateCheckout')
  
  const result = await authStore.register({
    nomeCompleto: form.nomeCompleto,
    email: form.email,
    senha: form.password
  })
  
  if (result.success) {
    const inviteToken = localStorage.getItem('inviteToken')
    if (inviteToken) {
      localStorage.removeItem('inviteToken')
      router.push(`/convite/${inviteToken}`)
    } else {
      router.push('/dashboard')
    }
  }
}
</script>
